import type { ThemeOptions } from '@mui/material/styles';
import type { ThemeMode } from './theme-vars';

export type ThemePreference = 'default' | 'light' | 'dark' | 'accessible';

type ThemePreferenceOption = {
  value: ThemePreference;
  label: string;
  description: string;
};

const headingFont = "'PT Serif', serif";
const bodyFont = "'Open Sans', sans-serif";

export const themePreferenceOptions: ReadonlyArray<ThemePreferenceOption> = [
  {
    value: 'default',
    label: 'Default',
    description: 'The Social Good look — a tinted canvas with raised cards and the brand status colors.',
  },
  {
    value: 'light',
    label: 'Light',
    description: 'A flat white canvas with darker text and status colors that all meet AA contrast.',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dark interface for low-light environments. Cards, modals, and inputs use dark surfaces.',
  },
  {
    value: 'accessible',
    label: 'Accessible',
    description: 'High-visibility text and controls for low-vision users, providing easier reading.',
  },
];

const themeModes: Record<ThemePreference, ThemeMode> = {
  default: 'default',
  light: 'light',
  dark: 'dark',
  accessible: 'accessible',
};

const brandThemeOverrides: ThemeOptions = {
  typography: {
    fontFamily: bodyFont,
    h1: { fontFamily: headingFont },
    h2: { fontFamily: headingFont },
    h3: { fontFamily: headingFont },
    h4: { fontFamily: headingFont },
    h5: { fontFamily: headingFont },
    h6: { fontFamily: headingFont },
  },
};

const accessibleThemeOverrides: ThemeOptions = {
  typography: {
    ...brandThemeOverrides.typography,
    fontWeightRegular: 600,
    body1: { fontWeight: 600 },
    body2: { fontWeight: 600 },
    caption: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, [tabindex]:focus-visible':
          {
            outline: '3px solid #0b4f8f',
            outlineOffset: '2px',
          },
      },
    },
  },
};

export function normalizeThemePreference(value: string | null | undefined): ThemePreference {
  let preference: ThemePreference = 'default';
  if (value === 'light' || value === 'dark' || value === 'accessible') {
    preference = value;
  }
  return preference;
}

export function resolveThemeMode(preference: ThemePreference): ThemeMode {
  return themeModes[preference];
}

export function resolveThemeOverrides(preference: ThemePreference): ThemeOptions {
  let overrides: ThemeOptions = brandThemeOverrides;
  if (preference === 'accessible') {
    overrides = accessibleThemeOverrides;
  }
  return overrides;
}
